import { useCallback, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ArrowDownLeft, ArrowUpRight, Radio } from 'lucide-react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import type { InteractionWithMetadata } from '@vagusx/clients';
import { TablePage } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InteractionDetailSheet } from '@/components/interactions/InteractionDetailSheet';
import { useCallDetailParam } from '@/hooks/use-call-detail-param';
import { useInteractions } from '@/hooks/use-interactions';
import { useLiveCallCount } from '@/hooks/use-live-calls';
import { formatElapsed } from '@/pages/calls/live/format';
import { resolveDateRange } from '@/lib/date-range';
import { CallHistoryFilters, type CallHistoryFilterState } from './CallHistoryFilters';
import type { CallHistorySearch } from './search-schema';
import { callDateBounds, callerName, outcomeLabel } from './format';
import { CountChip } from '../CountChip';

/**
 * Calls — every call this practice has placed or taken, newest first.
 *
 * Deliberately NOT the Activity view, which lists interactions of every
 * channel alongside the tasks they spawned. This page answers one question
 * ("what calls have we made, and how did they go?"), so it is scoped to
 * `channel=voice` and to calls that were actually dialed, and its columns
 * are the four an operator scans — when, who, how long, how it resolved.
 *
 * Clicking a row opens the same {@link InteractionDetailSheet} used from
 * referrals and tasks: recording, transcript, evaluation. The sheet is
 * driven by `?call=<id>`, so a specific call can be linked to or reloaded.
 */
export default function CallHistoryPage(): React.ReactElement {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/calls/' });

  const { openCallId, openCall, closeCall } = useCallDetailParam();
  // Reads the sidebar's poll (see useLiveCallCount) rather than adding a
  // second timer.
  const liveCount = useLiveCallCount({ poll: false });

  // The URL is the filter state, not a copy of it. Reading it back here is
  // what makes a reload, a Back, or a pasted link reproduce the list.
  const filters: CallHistoryFilterState = useMemo(
    () => ({
      direction: search.direction,
      outcomeCategory: search.outcome,
      dateRange: search.period
        ? { preset: search.period, from: search.dateFrom, to: search.dateTo }
        : { preset: 'all_time' },
    }),
    [search.direction, search.outcome, search.period, search.dateFrom, search.dateTo]
  );

  const { data, isLoading, error } = useInteractions({
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    channel: 'voice',
    dialed: true,
    direction: filters.direction,
    outcomeCategory: filters.outcomeCategory,
    ...callDateBounds(resolveDateRange(filters.dateRange)),
    search: search.searchTerm || undefined,
  });

  // `replace` so Back leaves the page rather than walking every filter the
  // user tried — the same call `/documents` makes. `to: '.'` merges into the
  // current search, which is what keeps `call` (the open detail sheet) set.
  // Undefined entries are dropped by the router, so clearing a filter
  // removes its param rather than writing an empty one.
  const updateSearch = useCallback(
    (updates: Partial<CallHistorySearch>): void => {
      void navigate({ to: '.', search: (prev) => ({ ...prev, ...updates }), replace: true });
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [navigate]
  );

  // Already debounced by `SearchInput`, so this writes once per pause.
  const handleSearchChange = (value: string): void => {
    updateSearch({ searchTerm: value || undefined });
  };

  const handleFilterChange = (next: CallHistoryFilterState): void => {
    const isCustom = next.dateRange.preset === 'custom';
    updateSearch({
      direction: next.direction,
      outcome: next.outcomeCategory as CallHistorySearch['outcome'],
      // All time is the absence of a period, so it is the absence of a param.
      period: next.dateRange.preset === 'all_time' ? undefined : next.dateRange.preset,
      // Only a custom pick carries dates of its own; a named period computes
      // its window on read, and stale bounds beside it would be ignored but
      // still sit in the URL looking authoritative.
      dateFrom: isCustom ? next.dateRange.from : undefined,
      dateTo: isCustom ? next.dateRange.to : undefined,
    });
  };

  const columns = useMemo<ColumnDef<InteractionWithMetadata>[]>(
    () => [
      {
        accessorKey: 'startedAt',
        header: 'When',
        meta: { headerClassName: 'w-[18%]' },
        cell: ({ row }) => {
          const startedAt = row.original.startedAt;
          if (!startedAt) return <span className="text-muted-foreground">—</span>;
          const at = new Date(startedAt);
          return (
            <span className="whitespace-nowrap tabular-nums">
              {format(at, 'MMM d, yyyy')}
              <span className="ml-2 text-muted-foreground">{format(at, 'h:mm a')}</span>
            </span>
          );
        },
      },
      {
        id: 'patient',
        header: 'Patient',
        meta: { headerClassName: 'w-[26%]', cellClassName: 'truncate' },
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate">{callerName(row.original)}</div>
            {row.original.phoneNumber && (
              <div className="truncate text-xs text-muted-foreground">
                {row.original.phoneNumber}
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'direction',
        header: 'Direction',
        meta: { headerClassName: 'w-[14%]' },
        cell: ({ row }) => {
          const direction = row.original.direction;
          if (!direction) return <span className="text-muted-foreground">—</span>;
          const Icon = direction === 'outbound' ? ArrowUpRight : ArrowDownLeft;
          return (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              {direction === 'outbound' ? 'Outbound' : 'Inbound'}
            </span>
          );
        },
      },
      {
        accessorKey: 'durationSeconds',
        header: 'Length',
        meta: { headerClassName: 'w-[12%]' },
        cell: ({ row }) => {
          const seconds = row.original.durationSeconds;
          // Null means the call never reached a clean end — it is still on
          // the line, or it died mid-call. "0:00" would claim it lasted no
          // time, which is a different and wrong statement.
          if (seconds == null) return <span className="text-muted-foreground">—</span>;
          return <span className="tabular-nums">{formatElapsed(seconds)}</span>;
        },
      },
      {
        accessorKey: 'outcomeCategory',
        header: 'Outcome',
        meta: { headerClassName: 'w-[20%]', cellClassName: 'truncate' },
        cell: ({ row }) => {
          const label = outcomeLabel(row.original);
          // Blank only when no disposition was recorded (a call that never
          // completed cleanly). Rendering the lifecycle
          // status here instead would read as an outcome — "Completed"
          // under Outcome says the call achieved something, which nobody
          // has claimed — so the truthful cell is an empty one.
          if (!label) {
            return (
              <span className="text-muted-foreground" title="No outcome recorded for this call">
                —
              </span>
            );
          }
          return (
            <Badge variant="outline" className="font-normal">
              {label}
            </Badge>
          );
        },
      },
    ],
    []
  );

  return (
    <TablePage
      title="Calls"
      description="Every call placed or answered, newest first."
      headerAction={
        <Button variant="outline" size="sm" asChild>
          <Link to="/calls/live">
            <Radio className="h-4 w-4" />
            Live Calls
            <CountChip count={liveCount} />
          </Link>
        </Button>
      }
      columns={columns}
      data={data?.items ?? []}
      total={data?.total}
      isLoading={isLoading}
      error={error}
      searchPlaceholder="Search by patient, number, agent, or summary..."
      searchValue={search.searchTerm ?? ''}
      onSearchChange={handleSearchChange}
      onRowClick={(row) => openCall(row.id)}
      emptyMessage="No calls match these filters."
      errorMessage="Failed to load call history. Please try again."
      pagination={pagination}
      onPaginationChange={setPagination}
      filterBar={<CallHistoryFilters value={filters} onChange={handleFilterChange} />}
      tableClassName="table-fixed"
    >
      <InteractionDetailSheet
        interactionId={openCallId}
        onOpenChange={(open) => !open && closeCall()}
      />
    </TablePage>
  );
}
